/**
 * @file 復習カードの見せ方 (純関数)。
 *
 * @what: まなぶ人のカードを「きょうの復習 (期日が今日以前)」と「この先の復習」に分け、
 *   期日順に並べる。結果の表示名と、期日の言い方 (きょう / あす / M/D) も持つ。
 * @why: 一覧は家族の全カードで返り、学習者の絞り込みと期日の判定は client が行う
 *   (learner と due_on は暗号化ペイロードの中でサーバの query に使えない)。
 * @failure: 純関数。retired のカードはどちらにも出さない。
 */

import type { StudyReviewCard, StudyReviewResultKind } from "../api/reviewCards";
import { formatLogDate } from "../api/dates";

export const REVIEW_RESULT_LABELS: Record<StudyReviewResultKind, string> = {
  ok: "できた",
  hard: "むずかしかった",
  ng: "できなかった",
};

/** まなぶ人の active なカードを期日順 (同じ期日は作った順) に。 */
export function activeCardsOf(
  cards: readonly StudyReviewCard[],
  learnerPersonId: string,
): StudyReviewCard[] {
  return cards
    .filter((c) => c.learner_person_id === learnerPersonId && c.status === "active")
    .sort((a, b) => a.due_on.localeCompare(b.due_on) || a.created_at.localeCompare(b.created_at));
}

/** 期日が today 以前のカード (きょうの復習)。 */
export function dueCards(cards: readonly StudyReviewCard[], today: string): StudyReviewCard[] {
  return cards.filter((c) => c.due_on <= today);
}

/** 期日が today より後のカード (この先の復習)。 */
export function upcomingCards(cards: readonly StudyReviewCard[], today: string): StudyReviewCard[] {
  return cards.filter((c) => c.due_on > today);
}

/** 期日の言い方。today を基準に「きょう」「あす」「M/D」、過ぎていれば「M/D (すぎています)」。 */
export function dueLabel(dueOn: string, today: string, tomorrow: string): string {
  if (dueOn === today) return "きょう";
  if (dueOn === tomorrow) return "あす";
  if (dueOn < today) return `${formatLogDate(dueOn, false)}（すぎています）`;
  return formatLogDate(dueOn, false);
}

/** 間隔の言い方 (1 → 翌日、3 → 3 日後、7 → 1 週間後、14〜 → 週、31〜 → か月)。 */
export function intervalLabel(days: number): string {
  if (days <= 1) return "翌日";
  if (days < 7) return `${days} 日後`;
  if (days < 30) return `${Math.round(days / 7)} 週間後`;
  if (days < 365) return `${Math.round(days / 30)} か月後`;
  return `${Math.round(days / 365)} 年後`;
}
